/**
 * Telegram WebApp SDK wrapper.
 *
 * We use a thin facade rather than calling `window.Telegram.WebApp` directly
 * everywhere for three reasons:
 *   1. Outside Telegram (local browser dev), the global is undefined. The
 *      facade returns no-op stubs so the rest of the app can run unchanged.
 *   2. `@telegram-apps/sdk` v3 has a slightly different surface than the raw
 *      JS bridge; we normalize on the raw bridge's API since it ships in
 *      `index.html` and is guaranteed to be present inside TG.
 *   3. It gives us a single place to add try/catch shielding — TMA bridges
 *      sometimes throw on older clients for unsupported methods.
 *
 * The raw `telegram-web-app.js` script (loaded in index.html) populates
 * `window.Telegram.WebApp` synchronously. We treat its presence as the
 * "running inside Telegram" detector.
 */

interface RawTelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; username?: string } };
  colorScheme: "light" | "dark";
  themeParams: Record<string, string | undefined>;
  ready: () => void;
  expand: () => void;
  close: () => void;
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  MainButton?: {
    text: string;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    setParams: (params: {
      text?: string;
      color?: string;
      text_color?: string;
      is_active?: boolean;
      is_visible?: boolean;
    }) => void;
  };
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  CloudStorage?: {
    setItem: (
      key: string,
      value: string,
      callback?: (err: string | null, ok: boolean) => void,
    ) => void;
    getItem: (
      key: string,
      callback: (err: string | null, value: string | null) => void,
    ) => void;
    removeItem: (
      key: string,
      callback?: (err: string | null, ok: boolean) => void,
    ) => void;
  };
  onEvent: (name: string, cb: (...args: unknown[]) => void) => void;
  offEvent: (name: string, cb: (...args: unknown[]) => void) => void;
  setHeaderColor: (c: string) => void;
  setBackgroundColor: (c: string) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: RawTelegramWebApp };
  }
}

function getRaw(): RawTelegramWebApp | null {
  const w = typeof window !== "undefined" ? window : undefined;
  return w?.Telegram?.WebApp ?? null;
}

/** True when running inside an actual Telegram client. */
export function isInTelegram(): boolean {
  const raw = getRaw();
  // initData is empty in non-TG contexts even if the script loaded.
  return raw !== null && typeof raw.initData === "string" && raw.initData.length > 0;
}

/** Synchronously available initData string (or empty when not in TG). */
export function getInitData(): string {
  return getRaw()?.initData ?? "";
}

/** Tell Telegram we're ready to be displayed; safe to call multiple times. */
export function tmaReady(): void {
  const raw = getRaw();
  if (!raw) return;
  try {
    raw.ready();
    raw.expand();
  } catch {
    /* older clients */
  }
}

/** Push the current theme variables onto :root so Tailwind can read them. */
export function applyTheme(): void {
  const raw = getRaw();
  if (!raw) return;
  const root = document.documentElement;
  const params = raw.themeParams ?? {};
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === "string" && v.length > 0) {
      // Convert snake_case to kebab and prefix.
      root.style.setProperty(`--tg-theme-${k.replace(/_/g, "-")}`, v);
    }
  }
  root.dataset["tgColorScheme"] = raw.colorScheme;
  // Match the header to the body so the inset bar doesn't look stitched on.
  try {
    raw.setHeaderColor(params["bg_color"] ?? "#ffffff");
    raw.setBackgroundColor(params["bg_color"] ?? "#ffffff");
  } catch {
    /* unsupported on older clients */
  }
}

/* --------------------------------- Haptics -------------------------------- */

export const haptics = {
  impact(style: "light" | "medium" | "heavy" | "soft" | "rigid" = "light"): void {
    try {
      getRaw()?.HapticFeedback?.impactOccurred(style);
    } catch {
      /* unsupported */
    }
  },
  notify(type: "success" | "warning" | "error"): void {
    try {
      getRaw()?.HapticFeedback?.notificationOccurred(type);
    } catch {
      /* unsupported */
    }
  },
  selection(): void {
    try {
      getRaw()?.HapticFeedback?.selectionChanged();
    } catch {
      /* unsupported */
    }
  },
};

/* ------------------------------- Main button ------------------------------ */

export const mainButton = {
  show(text: string, onClick: () => void): () => void {
    const raw = getRaw();
    if (!raw?.MainButton) {
      // Outside TG, return a noop disposer so callers can rely on it.
      return () => {};
    }
    const mb = raw.MainButton;
    mb.setParams({ text, is_visible: true, is_active: true });
    mb.onClick(onClick);
    mb.show();
    return () => {
      try {
        mb.offClick(onClick);
        mb.hide();
      } catch {
        /* ignore */
      }
    };
  },
  setLoading(loading: boolean): void {
    const mb = getRaw()?.MainButton;
    if (!mb) return;
    try {
      if (loading) mb.showProgress(false);
      else mb.hideProgress();
    } catch {
      /* ignore */
    }
  },
  setText(text: string): void {
    try {
      getRaw()?.MainButton?.setText(text);
    } catch {
      /* ignore */
    }
  },
};

/* ------------------------------- Back button ------------------------------ */

export const backButton = {
  show(onClick: () => void): () => void {
    const raw = getRaw();
    if (!raw?.BackButton) return () => {};
    const bb = raw.BackButton;
    bb.onClick(onClick);
    bb.show();
    return () => {
      try {
        bb.offClick(onClick);
        bb.hide();
      } catch {
        /* ignore */
      }
    };
  },
};

/* ------------------------------ CloudStorage ----------------------------- */

/**
 * Telegram CloudStorage is a 1KB key/value store that survives reinstall.
 * Outside TG (or on old clients without CloudStorage) we transparently fall
 * back to `localStorage`. The JWT lives here; messages do NOT — they're
 * always re-fetched from the server (per architecture Section 5 state
 * boundaries).
 */
function lsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(`tg.cs.${key}`);
  } catch {
    return null;
  }
}
function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(`tg.cs.${key}`, value);
  } catch {
    /* ignore quota */
  }
}
function lsRemove(key: string): void {
  try {
    window.localStorage.removeItem(`tg.cs.${key}`);
  } catch {
    /* ignore */
  }
}

export const cloudStorage = {
  async get(key: string): Promise<string | null> {
    // Outside TG, telegram-web-app.js still defines CloudStorage but its
    // methods throw WebAppMethodUnsupported. Gate on isInTelegram(), not
    // method presence.
    const cs = isInTelegram() ? getRaw()?.CloudStorage : null;
    if (!cs) return lsGet(key);
    return new Promise((resolve) => {
      try {
        cs.getItem(key, (err, value) => {
          if (err) resolve(lsGet(key));
          else resolve(value);
        });
      } catch {
        resolve(lsGet(key));
      }
    });
  },
  async set(key: string, value: string): Promise<void> {
    const cs = isInTelegram() ? getRaw()?.CloudStorage : null;
    if (!cs) {
      lsSet(key, value);
      return;
    }
    return new Promise((resolve) => {
      try {
        cs.setItem(key, value, () => resolve());
      } catch {
        lsSet(key, value);
        resolve();
      }
    });
  },
  async remove(key: string): Promise<void> {
    const cs = isInTelegram() ? getRaw()?.CloudStorage : null;
    if (!cs) {
      lsRemove(key);
      return;
    }
    return new Promise((resolve) => {
      try {
        cs.removeItem(key, () => resolve());
      } catch {
        lsRemove(key);
        resolve();
      }
    });
  },
};

/** Current color scheme. Defaults to `light` outside TG. */
export function getColorScheme(): "light" | "dark" {
  return getRaw()?.colorScheme ?? "light";
}
